// src/components/FittingAppointmentsAdmin.js

import React, { useState, useEffect } from "react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../firebase-config";
import "../style/FittingAppointmentsAdmin.css";

const FittingAppointmentsAdmin = () => {
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, confirmed, pending
  const [expandedId, setExpandedId] = useState(null);

  // Fetch appointments from Firestore
  useEffect(() => {
    const appointmentsRef = collection(db, "fittingAppointments");
    const q = query(appointmentsRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const appointmentsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAppointments(appointmentsData);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching appointments:", error);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  // Filter appointments
  const filteredAppointments = appointments.filter((apt) => {
    if (filter === "all") return true;
    if (filter === "confirmed") return apt.status === "confirmed";
    if (filter === "pending") return apt.status === "pending_payment";
    return true;
  });

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return timestamp;
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return "N/A";
    return `€${Number(price).toLocaleString()}`;
  };

  // Toggle expanded view
  const toggleExpanded = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Get status badge class
  const getStatusClass = (status) => {
    switch (status) {
      case "confirmed":
        return "faa-status-confirmed";
      case "pending_payment":
        return "faa-status-pending";
      default:
        return "faa-status-unknown";
    }
  };

  // Get payment status badge class
  const getPaymentClass = (paymentStatus) => {
    switch (paymentStatus) {
      case "paid":
        return "faa-payment-paid";
      case "pending":
        return "faa-payment-pending";
      default:
        return "faa-payment-unknown";
    }
  };

  if (isLoading) {
    return (
      <div className="faa-container">
        <div className="faa-loading">
          <div className="faa-spinner"></div>
          <p>Loading appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="faa-container">
      {/* Header */}
      <div className="faa-header">
        <h2>Fitting Appointments</h2>
        <div className="faa-stats">
          <span className="faa-stat">
            Total: <strong>{appointments.length}</strong>
          </span>
          <span className="faa-stat faa-stat-confirmed">
            Confirmed:{" "}
            <strong>
              {appointments.filter((a) => a.status === "confirmed").length}
            </strong>
          </span>
          <span className="faa-stat faa-stat-pending">
            Pending:{" "}
            <strong>
              {
                appointments.filter((a) => a.status === "pending_payment")
                  .length
              }
            </strong>
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="faa-filters">
        <button
          className={`faa-filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          All
        </button>
        <button
          className={`faa-filter-btn ${filter === "confirmed" ? "active" : ""}`}
          onClick={() => setFilter("confirmed")}
        >
          Confirmed
        </button>
        <button
          className={`faa-filter-btn ${filter === "pending" ? "active" : ""}`}
          onClick={() => setFilter("pending")}
        >
          Pending
        </button>
      </div>

      {/* Appointments List */}
      {filteredAppointments.length === 0 ? (
        <div className="faa-empty">
          <p>No appointments found.</p>
        </div>
      ) : (
        <div className="faa-list">
          {filteredAppointments.map((appointment) => (
            <div
              key={appointment.id}
              className={`faa-card ${expandedId === appointment.id ? "expanded" : ""}`}
            >
              {/* Card Header - Always visible */}
              <div
                className="faa-card-header"
                onClick={() => toggleExpanded(appointment.id)}
              >
                <div className="faa-card-main">
                  <div className="faa-customer-info">
                    <h3>{appointment.customerName || "Unknown"}</h3>
                    <p className="faa-email">{appointment.customerEmail}</p>
                  </div>
                  <div className="faa-appointment-info">
                    <p className="faa-date">{appointment.appointmentDate}</p>
                    <p className="faa-time">
                      {appointment.appointmentTime} (Paris)
                    </p>
                  </div>
                </div>
                <div className="faa-card-status">
                  <span
                    className={`faa-status-badge ${getStatusClass(appointment.status)}`}
                  >
                    {appointment.status === "confirmed"
                      ? "Confirmed"
                      : "Pending"}
                  </span>
                  <span
                    className={`faa-payment-badge ${getPaymentClass(appointment.paymentStatus)}`}
                  >
                    {appointment.paymentStatus === "paid" ? "Paid" : "Unpaid"}
                  </span>
                </div>
                <div className="faa-expand-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{
                      transform:
                        expandedId === appointment.id
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="#707070"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Card Details - Expandable */}
              {expandedId === appointment.id && (
                <div className="faa-card-details">
                  {/* Product Info */}
                  <div className="faa-detail-section">
                    <h4>Product</h4>
                    <div className="faa-product-info">
                      {appointment.productImage && (
                        <img
                          src={appointment.productImage}
                          alt={appointment.productName}
                          className="faa-product-image"
                        />
                      )}
                      <div className="faa-product-details">
                        <p className="faa-product-name">
                          {appointment.productName || "Not specified"}
                        </p>
                        <p className="faa-product-price">
                          Price: {formatPrice(appointment.productPrice)}
                        </p>
                        {appointment.productId && (
                          <p className="faa-product-id">
                            ID: {appointment.productId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Payment Info */}
                  <div className="faa-detail-section">
                    <h4>Payment</h4>
                    <div className="faa-detail-grid">
                      <div className="faa-detail-item">
                        <span className="faa-label">Deposit:</span>
                        <span className="faa-value">
                          €
                          {((appointment.depositAmount || 5000) / 100).toFixed(
                            2,
                          )}
                        </span>
                      </div>
                      <div className="faa-detail-item">
                        <span className="faa-label">Payment ID:</span>
                        <span className="faa-value faa-monospace">
                          {appointment.paymentIntentId || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div className="faa-detail-section">
                    <h4>Timestamps</h4>
                    <div className="faa-detail-grid">
                      <div className="faa-detail-item">
                        <span className="faa-label">Created:</span>
                        <span className="faa-value">
                          {formatDate(appointment.createdAt)}
                        </span>
                      </div>
                      {appointment.confirmedAt && (
                        <div className="faa-detail-item">
                          <span className="faa-label">Confirmed:</span>
                          <span className="faa-value">
                            {formatDate(appointment.confirmedAt)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="faa-actions">
                    <a
                      href={`mailto:${appointment.customerEmail}?subject=Your Fitting Appointment - ${appointment.appointmentDate}`}
                      className="faa-action-btn faa-email-btn"
                    >
                      Email Customer
                    </a>
                    {/* <a
                      href={`https://dashboard.stripe.com/payments/${appointment.paymentIntentId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="faa-action-btn faa-stripe-btn"
                    >
                      View in Stripe
                    </a> */}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FittingAppointmentsAdmin;
